import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Search as SearchIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import projectConfig from '../assets/configs/projectConfig';
import projectOrder from '../assets/configs/projectOrder';
import Seo from '../components/Seo';
import { withUtm } from '../utils/withUtm';
import { useLang } from '../utils/i18n';

const orderedProjects = projectOrder
  .map((id) => projectConfig.find((p) => p.id === id))
  .filter(Boolean);

const UI = {
  en: {
    seoTitle: 'Projects | Martín Antúnez',
    seoDesc: 'Data pipelines, ML systems, and open-source projects by Martín Antúnez.',
    overline: 'Selected Work',
    title: 'Projects',
    desc:
      'A curated collection spanning production-grade pipelines, ML systems, and this website. Filter, search, and explore.',
    searchPlaceholder: 'Search projects',
    tagPlaceholder: 'Filter by technology or topic',
    sortRecent: 'Recent',
    sortAZ: 'A → Z',
    ctaView: 'View',
  },
  es: {
    seoTitle: 'Proyectos | Martín Antúnez',
    seoDesc: 'Pipelines de datos, sistemas ML y proyectos open-source de Martín Antúnez.',
    overline: 'Trabajo destacado',
    title: 'Proyectos',
    desc:
      'Una colección curada de pipelines, sistemas ML y este sitio web. Filtra, busca y explora.',
    searchPlaceholder: 'Buscar proyectos',
    tagPlaceholder: 'Filtrar por tecnología o tema',
    sortRecent: 'Recientes',
    sortAZ: 'A → Z',
    ctaView: 'Ver',
  },
};

const LinkButton = ({ link }) => (
  <Tooltip title={link.name} placement="top">
    <IconButton
      size="small"
      component="a"
      href={withUtm(link.url, 'projects_page')}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
    >
      {link.icon ? (
        <Box sx={{ display: 'flex', alignItems: 'center', '& svg': { fontSize: 18 } }}>
          {link.icon}
        </Box>
      ) : (
        <OpenInNewIcon fontSize="inherit" />
      )}
    </IconButton>
  </Tooltip>
);

const getCategoryLabels = (category, lang) => {
  if (!category) return [];
  const arr = Array.isArray(category) ? category : [category];
  return arr.map((c) => (typeof c === 'string' ? c : c[lang] || c.en));
};

const ProjectCard = ({ project, lang = 'en' }) => {
  const desc = project.description_i18n?.[lang] || project.description_i18n?.en || '';
  const categoryLabels = getCategoryLabels(project.category, lang);

  const detailPath = project.slug ? `/projects/${project.slug}` : null;

  // Only show external icon links that are meaningful (not fork/watch clutter)
  const externalLinks = (project.links || []).filter(
    (l) => l.name !== 'fork' && l.name !== 'subscription'
  );

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Clickable image + content area → detail page */}
      <CardActionArea
        component={detailPath ? RouterLink : 'div'}
        to={detailPath || undefined}
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <Box sx={{ position: 'relative', pt: '56.25%', overflow: 'hidden', bgcolor: 'rgba(6,182,212,0.06)' }}>
          {project.image ? (
            <CardMedia
              component="img"
              image={project.image}
              alt={project.title}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'saturate(1.05) brightness(0.9)',
                transition: 'transform .3s ease',
                '.MuiCardActionArea-root:hover &': { transform: 'scale(1.03)' },
              }}
            />
          ) : (
            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #0a1628 0%, #06182e 60%, rgba(6,182,212,0.12) 100%)' }} />
          )}
        </Box>

        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {categoryLabels.length > 0 && (
            <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ columnGap: 0.5, rowGap: 0.75 }}>
              {categoryLabels.map((label) => (
                <Chip key={label} size="small" label={label} />
              ))}
            </Stack>
          )}
          <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: -0.2 }}>
            {project.title_i18n?.[lang] || project.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {desc}
          </Typography>
          {project.tags?.length > 0 && (
            <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ mt: 0.5, columnGap: 0.5, rowGap: 1 }}>
              {project.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    fontSize: 10,
                    height: 20,
                    bgcolor: 'rgba(6,182,212,0.08)',
                    color: '#7dd3fc',
                    border: '1px solid rgba(6,182,212,0.18)',
                    fontWeight: 600,
                  }}
                />
              ))}
            </Stack>
          )}
        </CardContent>
      </CardActionArea>

      <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1}>
          {externalLinks.map((l) => (
            <LinkButton key={l.name} link={l} />
          ))}
        </Stack>
        {detailPath && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            component={RouterLink}
            to={detailPath}
          >
            {UI[lang].ctaView}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default function Projects() {
  const [category, setCategory] = useState('All');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('recent');
  const [selectedTags, setSelectedTags] = useState([]);
  const [lang] = useLang();
  const t = UI[lang];

  const allTags = useMemo(() => {
    const tags = new Set();
    orderedProjects.forEach((p) => (p.tags || []).forEach((t) => tags.add(t)));
    return Array.from(tags).sort();
  }, []);

  const projects = useMemo(() => {
    const filtered = orderedProjects.filter((p) => {
      if (category !== 'All' && !getCategoryLabels(p.category, lang).includes(category)) return false;
      if (selectedTags.length > 0 && !selectedTags.every((tag) => (p.tags || []).includes(tag))) return false;
      return true;
    });

    const q = query.trim().toLowerCase();
    const searched = q
      ? filtered.filter(
          (p) =>
            (p.title || '').toLowerCase().includes(q) ||
            (p.description_i18n?.en || '').toLowerCase().includes(q) ||
            (p.description_i18n?.es || '').toLowerCase().includes(q)
        )
      : filtered;

    return [...searched].sort((a, b) => {
      if (sort === 'az') return (a.title || '').localeCompare(b.title || '');
      return 0;
    });
  }, [category, query, sort, lang, selectedTags]);

  const categoryChips = useMemo(() => {
    const labels = new Set(['All']);
    orderedProjects.forEach((p) => {
      getCategoryLabels(p.category, lang).forEach((label) => labels.add(label));
    });
    return Array.from(labels);
  }, [lang]);

  return (
    <>
      <Seo title={t.seoTitle} description={t.seoDesc} />

      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, #0a1628 0%, #060c14 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          py: { xs: 5, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2}>
            <Typography variant="overline" color="secondary.main" fontWeight={800}>
              {t.overline}
            </Typography>
            <Typography variant="h2" sx={{ fontWeight: 900, letterSpacing: -0.8 }}>
              {t.title}
            </Typography>
            <Typography color="text.secondary" maxWidth={720}>
              {t.desc}
            </Typography>

            {/* Category chips */}
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {categoryChips.map((c) => (
                <Chip
                  key={c}
                  label={c}
                  color={c === category ? 'secondary' : 'default'}
                  variant={c === category ? 'filled' : 'outlined'}
                  onClick={() => setCategory(c)}
                  sx={{ borderRadius: 10 }}
                />
              ))}
            </Stack>

            {/* Search + sort */}
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder={t.searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Divider flexItem orientation="vertical" />
              <Select size="small" value={sort} onChange={(e) => setSort(e.target.value)}>
                <MenuItem value="recent">{t.sortRecent}</MenuItem>
                <MenuItem value="az">{t.sortAZ}</MenuItem>
              </Select>
            </Stack>

            {/* Tag autocomplete filter */}
            <Autocomplete
              multiple
              size="small"
              options={allTags}
              value={selectedTags}
              onChange={(_, newValue) => setSelectedTags(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                    sx={{ bgcolor: 'rgba(6,182,212,0.12)', color: '#7dd3fc', border: '1px solid rgba(6,182,212,0.25)', fontWeight: 600, fontSize: 11 }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} placeholder={selectedTags.length === 0 ? t.tagPlaceholder : ''} />
              )}
              sx={{ maxWidth: 520 }}
            />
          </Stack>
        </Container>
      </Box>

      {/* Grid */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={3}>
          {projects.map((p) => (
            <Grid key={p.id} item xs={12} sm={6} md={4}>
              <ProjectCard project={p} lang={lang} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </>
  );
}



